import React from 'react';
import { Mail, Music, User, BookOpen, Video, X, Youtube, Heart } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@/contexts/ThemeContext';
import { SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetClose } from "@/components/ui/sheet";
import { Separator } from "@/components/ui/separator";
import { ScrollArea } from "@/components/ui/scroll-area";
import { AspectRatio } from '@/components/ui/aspect-ratio';
import { ThemeSwitcher } from '@/components/UI/ThemeSwitcher';
import { useIsMobile } from '@/hooks/use-mobile';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { storyCategories } from '@/data/storyCategories';
import { videoCategories } from '@/data/videoCategories';
import { useQuery } from '@tanstack/react-query';
import contentfulService from '@/services/contentfulService';
import contentfulVideoService from '@/services/contentfulVideoService';
interface SideMenuProps {
  onSubscribeOpen?: () => void;
}
const SideMenu: React.FC<SideMenuProps> = ({
  onSubscribeOpen
}) => {
  const navigate = useNavigate();
  const {
    resolvedTheme
  } = useTheme();
  const isMobile = useIsMobile();
  const {
    data: stories = []
  } = useQuery({
    queryKey: ['stories'],
    queryFn: contentfulService.getStories
  });
  const {
    data: videos = []
  } = useQuery({
    queryKey: ['videos'],
    queryFn: contentfulVideoService.getVideos
  });
  const latestArticles = stories.slice(0, 2);
  const latestVideos = videos.slice(0, 2);
  const handleSubscribeClick = () => {
    if (onSubscribeOpen) {
      onSubscribeOpen();
    }
  };
  return <SheetContent className={`${resolvedTheme === 'dark' ? 'bg-netflix-black text-white' : 'bg-background text-foreground'} flex flex-col h-full`}>
      <Separator className={`my-4 ${resolvedTheme === 'dark' ? 'bg-white/10' : 'bg-foreground/10'}`} />
      <div className="px-0 py-0">
        <ThemeSwitcher />
      </div>
      
      <ScrollArea className="flex-1 w-full">
        <div className="flex flex-col justify-between h-full">
          <div className="flex flex-col space-y-4 mt-4 pr-4">
            <Accordion type="single" collapsible className="w-full">
              <AccordionItem value="stories" className="border-none">
                <AccordionTrigger className={`flex items-center justify-between w-full text-left px-0 py-0 hover:no-underline ${resolvedTheme === 'dark' ? 'text-white/80 hover:text-white' : 'text-foreground/80 hover:text-foreground'} transition-colors`}>
                  <div className="flex items-center">
                    <BookOpen size={22} className="mr-5" />
                    <span className="text-xl font-chroma">Articles</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="pb-2 pt-0">
                  <div className="flex flex-col space-y-2">
                    {/* Latest Section */}
                    {latestArticles.length > 0 && <div className="mt-2.5">
                        <div className={`text-xs font-medium mb-2 ${resolvedTheme === 'dark' ? 'text-white/60' : 'text-foreground/60'}`}>
                          Latest
                        </div>
                        <div className="flex flex-col space-y-2">
                          {latestArticles.map((article: any) => {
                        const cover = typeof article.coverImage === 'string' ? article.coverImage : article.coverImage?.url;
                        const fallback = article.featuredImage?.url || article.thumbnail?.url || article.heroImage?.url || '/placeholder.svg';
                        const rawUrl = cover || fallback;
                        const imageUrl = rawUrl?.startsWith('//') ? `https:${rawUrl}` : rawUrl;
                        console.log('Article image URL:', imageUrl, 'Article:', article.title);
                        return <SheetClose key={article.slug} asChild>
                                <button onClick={() => navigate(`/stories/${article.slug}`)} className={`flex items-start space-x-2 text-left rounded-md transition-colors ${resolvedTheme === 'dark' ? 'hover:bg-white/5' : 'hover:bg-foreground/5'}`}>
                                  <div className="w-[6.25rem] flex-shrink-0 bg-muted overflow-hidden">
                                    <AspectRatio ratio={16 / 9}>
                                      <img src={imageUrl || '/placeholder.svg'} alt={article.title} className="w-full h-full object-cover" loading="lazy" onError={e => {
                                  console.log('Image failed to load:', imageUrl);
                                  e.currentTarget.src = '/placeholder.svg';
                                }} onLoad={() => console.log('Image loaded successfully:', imageUrl)} />
                                    </AspectRatio>
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <p className={`text-xs line-clamp-3 ${resolvedTheme === 'dark' ? 'text-white/80' : 'text-foreground/80'}`}>
                                      {article.title}
                                    </p>
                                  </div>
                                </button>
                              </SheetClose>;
                      })}
                        </div>
                      </div>}
                    
                    {storyCategories.filter(cat => cat.slug !== 'all' && cat.slug !== 'cars-of-everhouse').map(category => <div key={category.slug}>
                        <SheetClose asChild>
                          <button onClick={() => {
                        if (category.slug === 'editors-picks') {
                          navigate('/search?tag=editors-picks');
                        } else if (category.slug === 'houses-on-screen') {
                          navigate('/browse?category=houses-on-screen');
                        } else if (category.slug === 'studios') {
                          navigate('/browse?category=studios');
                        } else if (category.slug === 'considered') {
                          navigate('/browse?category=considered');
                        } else {
                          navigate(`/browse?category=${category.slug}`);
                        }
                      }} className={`block w-full text-left py-1 ${resolvedTheme === 'dark' ? 'text-white/70 hover:text-white' : 'text-foreground/70 hover:text-foreground'} transition-colors`}>
                            {category.title}
                          </button>
                        </SheetClose>
                        {category.slug === 'studios' && <>
                            <SheetClose asChild>
                              <button onClick={() => navigate('/browse?category=cars')} className={`block w-full text-left mt-1 py-1 ${resolvedTheme === 'dark' ? 'text-white/70 hover:text-white' : 'text-foreground/70 hover:text-foreground'} transition-colors`}>
                                Cars
                              </button>
                            </SheetClose>
                            <SheetClose asChild>
                              <button onClick={() => navigate('/browse')} className={`block w-full text-left mt-1 py-1 ${resolvedTheme === 'dark' ? 'text-white/70 hover:text-white' : 'text-foreground/70 hover:text-foreground'} transition-colors`}>
                                All Articles
                              </button>
                            </SheetClose>
                          </>}
                      </div>)}
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
            
            <Accordion type="single" collapsible className="w-full">
              <AccordionItem value="videos" className="border-none">
                <AccordionTrigger className={`flex items-center justify-between w-full text-left px-0 py-0 hover:no-underline ${resolvedTheme === 'dark' ? 'text-white/80 hover:text-white' : 'text-foreground/80 hover:text-foreground'} transition-colors`}>
                  <div className="flex items-center">
                    <Video size={22} className="mr-5" />
                    <span className="text-xl font-chroma">Videos</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="pb-2 pt-0">
                  <div className="flex flex-col space-y-2">
                    {latestVideos.length > 0 && <div className="mt-2.5">
                        <div className={`${resolvedTheme === 'dark' ? 'text-white/60' : 'text-foreground/60'} text-xs font-medium mb-2`}>
                          Latest
                        </div>
                        <div className="flex flex-col space-y-2">
                          {latestVideos.map((video: any) => {
                        const rawUrl = video.thumbnailUrl || video.thumbnail || '/placeholder.svg';
                        const imageUrl = rawUrl?.startsWith('//') ? `https:${rawUrl}` : rawUrl;
                        return <SheetClose key={video.slug} asChild>
                                <button onClick={() => navigate(`/videos/${video.slug}`)} className={`flex items-start space-x-2 text-left rounded-md transition-colors ${resolvedTheme === 'dark' ? 'hover:bg-white/5' : 'hover:bg-foreground/5'}`}>
                                  <div className="w-[6.25rem] flex-shrink-0 bg-muted overflow-hidden">
                                    <AspectRatio ratio={16 / 9}>
                                      <img src={imageUrl || '/placeholder.svg'} alt={video.title} className="w-full h-full object-cover" loading="lazy" onError={e => {
                                  e.currentTarget.src = '/placeholder.svg';
                                }} />
                                    </AspectRatio>
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <p className={`${resolvedTheme === 'dark' ? 'text-white/80' : 'text-foreground/80'} text-xs line-clamp-3`}>
                                      {video.title}
                                    </p>
                                  </div>
                                </button>
                              </SheetClose>;
                      })}
                        </div>
                      </div>}
                    {videoCategories.map(category => <SheetClose key={category.slug} asChild>
                        <button onClick={() => navigate(category.slug === 'all' ? '/videos' : `/videos?category=${category.slug}`)} className={`block w-full text-left py-1 ${resolvedTheme === 'dark' ? 'text-white/70 hover:text-white' : 'text-foreground/70 hover:text-foreground'} transition-colors`}>
                          {category.title}
                        </button>
                      </SheetClose>)}
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
            
            <SheetClose asChild>
              <button onClick={() => navigate('/playlists')} className={`flex items-center space-x-2 text-left ${resolvedTheme === 'dark' ? 'text-white/80 hover:text-white' : 'text-foreground/80 hover:text-foreground'} transition-colors`}>
                <Music size={22} className="mr-3" />
                <span className="text-xl font-chroma">Playlists</span>
              </button>
            </SheetClose>
            
            

            
            <SheetClose asChild>
              <button onClick={handleSubscribeClick} className={`flex items-center space-x-2 text-left ${resolvedTheme === 'dark' ? 'text-white/80 hover:text-white' : 'text-foreground/80 hover:text-foreground'} transition-colors`}>
                <Mail size={22} className="mr-3" />
                <span className="text-xl font-chroma">Subscribe</span>
              </button>
            </SheetClose>
            
            <SheetClose asChild>
              <button onClick={() => navigate('/profile')} className={`flex items-center space-x-2 text-left ${resolvedTheme === 'dark' ? 'text-white/80 hover:text-white' : 'text-foreground/80 hover:text-foreground'} transition-colors`}>
                <User size={22} className="mr-3" />
                <span className="text-xl font-chroma">Profile</span>
              </button>
            </SheetClose>
          </div>
          
          <div className="flex flex-col space-y-4 mt-8 pr-4 pb-4 my-[34px]">
            {/* Featured YouTube video */}
            <div className="flex items-center gap-1 mb-1 text-[11px] leading-none text-foreground">
              <Youtube size={14} className="text-destructive" />
              <span className="font-medium text-sm">Most Popular YT Video</span>
            </div>
            <a href="https://www.youtube.com/watch?v=4XumTRutQd0" target="_blank" rel="noopener noreferrer" aria-label="Watch our best-performing YouTube video on YouTube" className="group block focus:outline-none focus:ring-2 focus:ring-primary rounded-lg">
              <AspectRatio ratio={16 / 9}>
                <img src="https://i.ytimg.com/vi/4XumTRutQd0/hqdefault.jpg" alt="Everhouse YouTube top video thumbnail — 4XumTRutQd0" loading="lazy" className="h-full w-full object-cover rounded-lg border border-border" />
                <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-black/0 via-black/10 to-black/40 opacity-0 group-hover:opacity-100 transition-opacity" />
              </AspectRatio>
            </a>
            <p className="mt-2 text-muted-foreground text-xs">1M views • 10 months ago</p>
          </div>
        </div>
      </ScrollArea>
    </SheetContent>;
};
export default SideMenu;